import { useEffect, useMemo, useState } from 'react'
import { Globe } from 'lucide-react'
import clsx from 'clsx'
import type { WebSearchMode } from '@/lib/settings'
import { BottomSheet } from '@/components/BottomSheet'

type WebSearchModeToggleProps = {
  mode: WebSearchMode
  onChange: (mode: WebSearchMode) => void
  disabled?: boolean
  className?: string
  tone?: 'default' | 'home'
  // Платная фича: на бесплатном тарифе веб-поиск закрыт. Показываем бейдж «Pro»,
  // клик ведёт к апгрейду (onLockedClick), а не открывает выбор режима.
  locked?: boolean
  onLockedClick?: () => void
}

const MODES: Array<{ id: WebSearchMode; label: string; description: string }> = [
  {
    id: 'off',
    label: 'OFF',
    description: 'Только база законов и загруженные документы.',
  },
  {
    id: 'auto',
    label: 'AUTO',
    description: 'Ассистент сам решает, когда нужен веб-поиск.',
  },
  {
    id: 'on',
    label: 'ON',
    description: 'Всегда подключает поиск в интернете.',
  },
]

export function WebSearchModeToggle({
  mode,
  onChange,
  disabled = false,
  className,
  tone = 'default',
  locked = false,
  onLockedClick,
}: WebSearchModeToggleProps) {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open])

  const modeVisual = useMemo(() => {
    if (tone === 'home') {
      if (mode === 'off') return 'text-white/45'
      if (mode === 'auto') return 'text-white/70'
      return 'text-white'
    }
    if (mode === 'off') return 'text-telegram-hint/55'
    if (mode === 'auto') return 'text-telegram-hint'
    return 'text-telegram-button'
  }, [mode, tone])

  const buttonSurface = tone === 'home'
    ? 'bg-white/18 border-white/30'
    : 'glass-icon border-telegram-hint/20'

  const selectedMode = MODES.find((item) => item.id === mode) || MODES[1]

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => { if (locked) { onLockedClick?.(); return } setOpen(true) }}
        title={locked ? 'Веб-поиск доступен на тарифах Лайт и Про' : `Режим веб-поиска: ${selectedMode.label}`}
        className={clsx(
          'shrink-0 inline-flex items-center gap-1.5 rounded-xl border px-2 h-[38px]',
          'transition-all active:scale-95',
          buttonSurface,
          locked ? (tone === 'home' ? 'text-white/70' : 'text-telegram-hint') : modeVisual,
          disabled && 'opacity-50 cursor-not-allowed',
          className,
        )}
      >
        <Globe size={15} />
        <span className="text-[11px] font-semibold">Веб</span>
        {locked && (
          <span className="text-[8px] font-extrabold leading-none px-1 py-0.5 rounded-full bg-[#ee3f58] text-white">PRO</span>
        )}
      </button>

      <BottomSheet
        open={open}
        onClose={() => setOpen(false)}
        maxBodyHeight="58vh"
        title={(
          <span className="inline-flex items-center gap-1.5">
            <Globe size={14} />
            Веб-поиск
          </span>
        )}
        description="Управляет использованием интернет-источников в ответе."
      >
        <div className="flex flex-col gap-2 py-1">
          {MODES.map((item) => {
            const active = item.id === mode
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  onChange(item.id)
                  setOpen(false)
                }}
                className={clsx(
                  'w-full text-left rounded-xl px-3 py-2 transition-all active:scale-[0.99]',
                  active
                    ? 'bg-telegram-button text-white'
                    : 'glass-icon text-telegram-text',
                )}
              >
                <p className="text-[11px] font-semibold">{item.label}</p>
                <p className={clsx('text-[10px] mt-0.5', active ? 'text-white/85' : 'text-telegram-hint')}>
                  {item.description}
                </p>
              </button>
            )
          })}
        </div>
      </BottomSheet>
    </>
  )
}
